import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { DIVISIONS, GUIDES } from "@/data/site";
import { subscribeNewsletter } from "@/lib/api";

const linkCls = "text-sm text-cream/60 transition-colors hover:text-cream";
const LEGAL = [
  { label: "Warranty", to: "/legal/warranty" },
  { label: "Delivery", to: "/legal/delivery-information" },
  { label: "Returns", to: "/legal/returns" },
  { label: "Terms & Conditions", to: "/legal/terms-and-conditions" },
  { label: "Privacy Policy", to: "/legal/privacy-policy" },
  { label: "Accessibility", to: "/legal/accessibility" },
  { label: "Timber Sourcing", to: "/legal/timber-sourcing" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const logout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`);
    } finally {
      window.location.reload();
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list", { description: "Timber news, care guides and new products." });
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-ink text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-[1500px] px-5 pb-10 pt-20 md:px-10">
        <div className="grid gap-12 border-b border-cream/10 pb-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-kwila">Stay in the grain</p>
            <h3 className="mt-3 font-display text-3xl leading-tight md:text-4xl">
              Timber news, care guides & new arrivals.
            </h3>
            <form onSubmit={subscribe} className="mt-6 flex max-w-md gap-2" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full rounded-full border border-cream/20 bg-transparent px-5 py-3 text-sm outline-none placeholder:text-cream/40 focus:border-kwila"
              />
              <button
                data-testid="newsletter-submit-button"
                disabled={busy}
                className="flex shrink-0 items-center gap-2 rounded-full bg-kwila px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] transition-colors hover:bg-cream hover:text-ink"
              >
                Join <ArrowRight size={15} />
              </button>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7 lg:pl-10">
            <div>
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cream/40">Divisions</p>
              <ul className="space-y-2.5">
                {DIVISIONS.map((d) => (
                  <li key={d.key}>
                    <Link to={d.path} data-testid={`footer-link-${d.key}`} className={linkCls}>
                      {d.name}
                    </Link>
                  </li>
                ))}
                <li><Link to="/shop" data-testid="footer-link-shop" className={linkCls}>Shop All</Link></li>
                <li><Link to="/kwila-merbau" data-testid="footer-link-kwila" className={linkCls}>What is Kwila?</Link></li>
              </ul>
            </div>
            <div>
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cream/40">Company</p>
              <ul className="space-y-2.5">
                <li><Link to="/about" data-testid="footer-link-about" className={linkCls}>About Mr Kwila</Link></li>
                <li><Link to="/trade" data-testid="footer-link-trade" className={linkCls}>Trade & Reseller</Link></li>
                <li><Link to="/request-quote" data-testid="footer-link-quote" className={linkCls}>Request a Quote</Link></li>
                <li><Link to="/contact" data-testid="footer-link-contact" className={linkCls}>Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cream/40">Guides</p>
              <ul className="space-y-2.5">
                {GUIDES.slice(0, 4).map((g) => (
                  <li key={g.slug}>
                    <Link to={`/guides/${g.slug}`} data-testid={`footer-guide-${g.slug}`} className={linkCls}>
                      {g.title.length > 28 ? `${g.title.slice(0, 28)}…` : g.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="grid gap-8 border-b border-cream/10 py-10 text-sm text-cream/60 md:grid-cols-3">
          <p className="flex items-start gap-3">
            <MapPin size={16} className="mt-0.5 shrink-0 text-kwila" />
            Warehouse & distribution — Archerfield, Brisbane QLD, Australia
          </p>
          <p className="flex items-start gap-3">
            <Phone size={16} className="mt-0.5 shrink-0 text-kwila" />
            Phone — [to be confirmed by client]
          </p>
          <p className="flex items-start gap-3">
            <Mail size={16} className="mt-0.5 shrink-0 text-kwila" />
            Email — [to be confirmed by client]
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 pt-8 text-[11px] uppercase tracking-[0.18em] text-cream/35 md:flex-row">
          <p>© {new Date().getFullYear()} Mr Kwila Outdoor Furniture Pty Ltd</p>
          <p>Timber · Doors · Furniture — PNG made, Brisbane distributed</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {LEGAL.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="transition-colors hover:text-cream">
                {l.label}
              </Link>
            ))}
            <button
              data-testid="footer-logout-button"
              onClick={logout}
              className="border-l border-cream/20 pl-4 transition-colors hover:text-kwila"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
